// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Divider, Group, Modal, ScrollArea, Stack, Text, Textarea } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference } from '@medplum/core';
import type { Encounter, Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconEdit, IconPhone, IconPlus } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { withTenantScope } from '../../utils/tenant';
import { readPhoneEncounterNotes, setPhoneEncounterNotes } from './phone-encounter';

interface Props {
  opened: boolean;
  patient: Patient;
  encounter?: Encounter;
  onClose: () => void;
  onSaved: () => void;
}

const PHONE_TYPE = {
  system: 'https://polarissleep.com/fhir/encounter-type',
  code: 'phone-encounter',
  display: 'Phone Encounter',
};
const PHONE_ENCOUNTER_SECURITY = {
  system: 'https://polarissleep.com/fhir/security',
  code: 'provider-only',
  display: 'Provider-only clinical note',
};

function practitionerDisplay(profile: Practitioner | undefined): string | undefined {
  const name = profile?.name?.[0];
  return name ? [name.prefix?.join(' '), name.given?.join(' '), name.family].filter(Boolean).join(' ') : undefined;
}

function noteLabel(encounter: Encounter | undefined, editing: boolean): string {
  if (!encounter) {
    return 'Note';
  }
  return editing ? 'Edit note' : 'Add note';
}

function actionLabel(encounter: Encounter | undefined, editing: boolean): string {
  if (editing) {
    return 'Save Changes';
  }
  return encounter ? 'Add Note' : 'Create Phone Encounter';
}

function notificationTitle(encounter: Encounter | undefined, editing: boolean): string {
  if (!encounter) {
    return 'Phone encounter created';
  }
  return editing ? 'Note updated' : 'Note added';
}

function actionIcon(encounter: Encounter | undefined, editing: boolean): JSX.Element {
  if (editing) {
    return <IconEdit size={16} />;
  }
  return encounter ? <IconPlus size={16} /> : <IconPhone size={16} />;
}

export function PhoneEncounterModal({ opened, patient, encounter, onClose, onSaved }: Props): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [note, setNote] = useState('');
  const [editingIndex, setEditingIndex] = useState<number>();
  const [saving, setSaving] = useState(false);

  const close = (): void => {
    setNote('');
    setEditingIndex(undefined);
    onClose();
  };

  async function save(): Promise<void> {
    if (!note.trim() || !profile?.id) {
      return;
    }
    setSaving(true);
    const now = new Date().toISOString();
    const author = { reference: `Practitioner/${profile.id}`, display: practitionerDisplay(profile) };
    try {
      if (encounter?.id) {
        const notes = readPhoneEncounterNotes(encounter);
        const nextNotes =
          editingIndex === undefined
            ? [...notes, { authorReference: author, time: now, text: note.trim() }]
            : notes.map((entry, index) => (index === editingIndex ? { ...entry, text: note.trim() } : entry));
        await medplum.updateResource(
          withTenantScope({
            ...encounter,
            extension: setPhoneEncounterNotes(encounter, nextNotes),
          })
        );
      } else {
        await medplum.createResource(
          withTenantScope({
            resourceType: 'Encounter',
            status: 'finished',
            class: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'PHON', display: 'phone' },
            type: [{ coding: [PHONE_TYPE], text: 'Phone Encounter' }],
            meta: { security: [PHONE_ENCOUNTER_SECURITY] },
            subject: createReference(patient),
            participant: [{ individual: author }],
            period: { start: now, end: now },
            extension: setPhoneEncounterNotes({ extension: [] }, [
              { authorReference: author, time: now, text: note.trim() },
            ]),
          } satisfies Encounter)
        );
      }
      showNotification({
        color: 'green',
        title: notificationTitle(encounter, editingIndex !== undefined),
        message: 'Saved to encounter history.',
      });
      close();
      onSaved();
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Unable to save phone encounter',
        message: err instanceof Error ? err.message : 'Try again.',
      });
    } finally {
      setSaving(false);
    }
  }

  const editing = editingIndex !== undefined;

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={
        <Group gap="xs">
          <IconPhone size={18} />
          <Text fw={600}>{encounter ? 'Phone Encounter Note' : 'Create Phone Encounter'}</Text>
        </Group>
      }
      centered
      size="md"
    >
      <Stack>
        {encounter && (
          <ScrollArea.Autosize mah={220}>
            <Stack gap="sm">
              {readPhoneEncounterNotes(encounter).map((entry, index) => (
                <Group
                  key={`${entry.time ?? 'note'}-${index}`}
                  justify="space-between"
                  align="flex-start"
                  wrap="nowrap"
                >
                  <Stack gap={2}>
                    <Text size="sm">{entry.text}</Text>
                    <Text size="xs" c="dimmed">
                      {entry.authorReference?.display ?? entry.authorReference?.reference ?? 'Provider'} ·{' '}
                      {entry.time ? new Date(entry.time).toLocaleString() : ''}
                    </Text>
                  </Stack>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconEdit size={14} />}
                    onClick={() => {
                      setEditingIndex(index);
                      setNote(entry.text ?? '');
                    }}
                  >
                    Edit
                  </Button>
                </Group>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
        {encounter && <Divider />}
        <Textarea
          label={noteLabel(encounter, editing)}
          placeholder="Document phone encounter details…"
          minRows={5}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
          autoFocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          {editingIndex !== undefined && (
            <Button
              variant="subtle"
              onClick={() => {
                setEditingIndex(undefined);
                setNote('');
              }}
            >
              Cancel Edit
            </Button>
          )}
          <Button
            leftSection={actionIcon(encounter, editing)}
            onClick={() => save().catch(console.error)}
            loading={saving}
            disabled={!note.trim() || !profile?.id}
          >
            {actionLabel(encounter, editing)}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
